import React, { useState, useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    TextField,
    Paper,
    IconButton,
    Icon,
    InputAdornment,
    ClickAwayListener
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import * as authActions from 'app/auth/store/actions';

const OwnerSelection = ({ value, onChange, label = "Resource Owner", disabled = false, filteredUsers = null, adminOnly = false }) => {
    const dispatch = useDispatch();
    const currentUser = useSelector(({ auth }) => auth.user);
    const allUsers = useSelector(({ auth }) => auth.user.users) || [];
    
    // Use filtered users if provided, otherwise use all users
    // When adminOnly is true, only show users with admin flag
    const baseUsers = filteredUsers || allUsers;
    const users = adminOnly ? baseUsers.filter(u => u.admin) : baseUsers;
    
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState('');
    
    // Only show owner selection for super-admin users
    const isSuperAdmin = currentUser.role && currentUser.role.includes('super-admin');
    
    // Filter users based on search term
    const searchFilteredUsers = React.useMemo(() => {
        if (!searchTerm) {
            return users;
        }
        return users.filter(user => 
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [users, searchTerm]);
    
    useEffect(() => {
        if (isSuperAdmin) {
            // Fetch users if not already loaded (only when using allUsers, not filteredUsers)
            if (!filteredUsers && allUsers.length === 0) {
                dispatch(authActions.getUsers());
            }
            
            // Set default value to current user if not already set
            if (!value && currentUser.username) {
                onChange(currentUser.username);
            }
        }
    }, [isSuperAdmin, filteredUsers, allUsers.length, value, currentUser.username, onChange, dispatch]);
    
    // Update display value when value changes
    useEffect(() => {
        if (value) {
            const selectedUser = users.find(user => user.name === value);
            if (selectedUser) {
                setDisplayValue(`${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.name})`);
            } else {
                setDisplayValue(value);
            }
        } else {
            setDisplayValue('');
        }
    }, [value, users]);
    
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setDropdownOpen(true);
    };
    
    const handleSelectUser = (userName) => {
        onChange(userName);
        setSearchTerm('');
        setDropdownOpen(false);
    };
    
    const toggleDropdown = () => {
        if (!disabled) {
            setDropdownOpen(!dropdownOpen);
        }
    };
    
    const clearSearch = () => {
        setSearchTerm('');
    };

    const handleClickAway = () => {
        setDropdownOpen(false);
        setSearchTerm('');
    };
    
    // If not super-admin, don't render anything and automatically use current user
    if (!isSuperAdmin) {
        // Ensure the owner is set to current user for non-super-admins
        if (!value && currentUser.username && onChange) {
            onChange(currentUser.username);
        }
        return null;
    }
    
    return (
        <Box mb={2}>
            <ClickAwayListener onClickAway={handleClickAway}>
            <Box style={{ position: 'relative' }}>
                <TextField
                    label={label}
                    variant="outlined"
                    fullWidth
                    required
                    value={dropdownOpen ? searchTerm : displayValue}
                    onClick={disabled ? undefined : toggleDropdown}
                    onChange={handleSearchChange}
                    placeholder="Search users..."
                    disabled={users.length === 0 || disabled}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {searchTerm && (
                                    <IconButton 
                                        size="small" 
                                        onClick={clearSearch}
                                        style={{ padding: '4px' }}
                                    >
                                        <Icon fontSize="small">clear</Icon>
                                    </IconButton>
                                )}
                                <IconButton onClick={toggleDropdown}>
                                    <Icon>{dropdownOpen ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {dropdownOpen && !disabled && (
                    <Paper 
                        style={{ 
                            position: 'absolute', 
                            zIndex: 1000, 
                            width: '100%', 
                            maxHeight: 300, 
                            overflow: 'auto' 
                        }}
                    >
                        {searchFilteredUsers.map((user) => (
                            <MenuItem
                                key={user.id}
                                value={user.name}
                                selected={value === user.name}
                                onClick={() => handleSelectUser(user.name)}
                            >
                                <Box>
                                    <Typography variant="body1">
                                        {user.firstName} {user.lastName} ({user.name})
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {user.email} - {user.role?.join(', ')}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                        {searchFilteredUsers.length === 0 && (
                            <MenuItem disabled>
                                No matching users
                            </MenuItem>
                        )}
                    </Paper>
                )}
            </Box>
            </ClickAwayListener>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                As a super-admin, you can create this resource on behalf of any user. The selected user will be the owner.
            </Typography>
        </Box>
    );
};

export default OwnerSelection;
